import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { FreightBookingFormState } from '@shipstation/client-common/components/freight-booking-wizard';
import { getChangedFieldPaths } from '@shipstation/client-common/utils/get-changed-field-paths';
import { useEnableLtlWizardFormChangeTracking } from '../../../../feature-flags/hooks/use-enable-ltl-wizard-form-change-tracking';
import { Segment } from '../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../tracking-events';
import type { LtlFreightBookingSource } from '../../tracking-events';

type UseTrackFreightBookingWizardChangeParams = {
  formStateRef: MutableRefObject<FreightBookingFormState>;
  fulfillmentPlanIdRef: MutableRefObject<string | null>;
  sourceRef: MutableRefObject<LtlFreightBookingSource | undefined>;
  workflowGuidRef: MutableRefObject<string>;
};

export const useTrackFreightBookingWizardChange = ({
  formStateRef,
  fulfillmentPlanIdRef,
  sourceRef,
  workflowGuidRef,
}: UseTrackFreightBookingWizardChangeParams): ((next: FreightBookingFormState) => void) => {
  const isEnabled = useEnableLtlWizardFormChangeTracking();

  return useCallback(
    (next: FreightBookingFormState) => {
      if (!isEnabled) return;

      const fieldsChanged = getChangedFieldPaths(formStateRef.current, next);

      // Deferred to the end of the event queue so tracking calls never compete with
      // the form's own in-flight requests (autosave, re-quote, etc.) for the main thread.
      fieldsChanged.forEach((fieldChanged) => {
        setTimeout(() => {
          Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_FORM_CHANGED, {
            fulfillmentPlanId: fulfillmentPlanIdRef.current,
            source: sourceRef.current,
            workflowGuid: workflowGuidRef.current,
            fieldChanged,
          });
        }, 0);
      });
    },
    [isEnabled, formStateRef, fulfillmentPlanIdRef, sourceRef, workflowGuidRef],
  );
};
